/**
 * A video for a damage report, prepared in the browser before anything is
 * uploaded (stage 2 of docs/plans/2026-10-12-medien.md):
 *
 *   1. read with mediabunny (WebCodecs, no server) and refused when longer than
 *      VIDEO_MAX_SECONDS — said, not cut silently;
 *   2. re-encoded to H.264/AAC with the long edge at VIDEO_MAX_EDGE (720p is
 *      enough for a phone screen), `tags: {}` so the input's metadata is NOT
 *      copied (mediabunny copies it by default);
 *   3. every metadata box neutralised once more (`stripMp4Metadata`) and the
 *      result checked with the server's own rule (`inspectVideo`);
 *   4. a poster frame drawn from the result as the thumbnail (JPEG via canvas,
 *      no metadata), like a photo's.
 *
 * Progress goes to `onProgress` (0…1) so the form can show it. mediabunny is
 * loaded on first use only; it does not belong in the main bundle.
 */
import { inspectVideo, stripMp4Metadata, VIDEO_MAX_EDGE, VIDEO_MAX_SECONDS } from '@fsv/core';
import { tx, type NewImage } from '@fsv/data';

/** A refusal the person can act on; the message is ready to show. */
export class VideoRefused extends Error {}

const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

export async function prepareVideo(file: File, onProgress: (fraction: number) => void): Promise<NewImage> {
  const mb = await import('mediabunny');
  const input = new mb.Input({ source: new mb.BlobSource(file), formats: mb.ALL_FORMATS });
  const track = await input.getPrimaryVideoTrack();
  if (!track) throw new VideoRefused(tx.video.notVideo);
  const seconds = await input.computeDuration();
  if (seconds > VIDEO_MAX_SECONDS + 0.5) throw new VideoRefused(tx.video.tooLong(VIDEO_MAX_SECONDS, Math.round(seconds)));

  const scale = Math.min(1, VIDEO_MAX_EDGE / Math.max(track.displayWidth, track.displayHeight));
  const width = even(track.displayWidth * scale);
  const height = even(track.displayHeight * scale);
  const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: 'in-memory' }), target: new mb.BufferTarget() });
  const conversion = await mb.Conversion.init({
    input,
    output,
    video: { width, height, fit: 'contain', codec: 'avc', bitrate: 2_500_000 },
    // With sound: people describe the damage while filming (owner, 13.10.2026).
    audio: { codec: 'aac', bitrate: 96_000 },
    // No metadata of the original: it carries where the video was filmed.
    tags: {},
    showWarnings: false,
  });
  if (!conversion.isValid) throw new VideoRefused(tx.video.cannotConvert);
  conversion.onProgress = (p) => onProgress(Math.min(1, Math.max(0, p)));
  await conversion.execute();
  const buffer = output.target.buffer;
  if (!buffer) throw new VideoRefused(tx.video.cannotConvert);

  const bytes = stripMp4Metadata(new Uint8Array(buffer));
  const verdict = inspectVideo(bytes);
  if (!verdict.ok) throw new VideoRefused(verdict.reason === 'too_large' ? tx.video.tooLarge : tx.video.cannotConvert);

  // The poster from the result itself, decoded by mediabunny: a <video> element needs the
  // page's media policy and a visible tab, and stalls otherwise (seen 13.10.2026).
  const thumbDataUrl = await posterOf(mb, bytes, width, height);
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'video/mp4' }));
  return { dataUrl: url, thumbDataUrl, width, height, isOverview: false, kind: 'video', durationMs: Math.round(seconds * 1000) };
}

/** A JPEG of the frame at 0.1 s, 400 px on the long edge — the video's thumbnail, like a photo's. */
async function posterOf(mb: typeof import('mediabunny'), bytes: Uint8Array, width: number, height: number): Promise<string> {
  const input = new mb.Input({ source: new mb.BufferSource(bytes), formats: mb.ALL_FORMATS });
  const track = await input.getPrimaryVideoTrack();
  if (!track) throw new VideoRefused(tx.video.cannotConvert);
  const scale = Math.min(1, 400 / Math.max(width, height));
  const sink = new mb.CanvasSink(track, { width: Math.max(2, Math.round(width * scale)), height: Math.max(2, Math.round(height * scale)), fit: 'contain' });
  const frame = (await sink.getCanvas(0.1)) ?? (await sink.getCanvas(0));
  if (!frame) throw new VideoRefused(tx.video.cannotConvert);
  // Through a plain canvas: an OffscreenCanvas has no toDataURL.
  const canvas = document.createElement('canvas');
  canvas.width = frame.canvas.width;
  canvas.height = frame.canvas.height;
  canvas.getContext('2d')?.drawImage(frame.canvas, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.75);
}
